/**
 * ChannelProvider — состояние Канала на уровне Layout.
 *
 * Держит:
 *   - открыт ли Канал и на каком сообщении фокус (deep-link из пуша);
 *   - контекст экрана (Объект / Работа), который страница объявляет через
 *     useChannelScope — по нему Канал открывается отфильтрованным;
 *   - бейдж: { addressed, has_new } из GET /channel/unread;
 *   - «упомянуть из контакта Работы»: canMention / mentionInChannel — кнопка в
 *     блоке контактов открывает Канал этой Работы с готовым «@Имя» в поле;
 *   - ОДНО соединение SSE на вкладку (fetch + ReadableStream) с откатом на
 *     поллинг, если поток не держится (прокси, корпоративные сети).
 *
 * Слушатели событий (лента) подписываются через subscribe(): провайдер не
 * знает, что лента делает с событием, — только раздаёт {type, id, root_id}.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, openChannelStream } from '../../api/client';
import { usePerm } from '../../hooks/usePerm';
import { track } from '../../analytics';

const ChannelCtx = createContext(null);

const UNREAD_POLL_MS = 30_000;   // синхронно с бейджами Layout и Центром уведомлений
const FALLBACK_POLL_MS = 7_000;  // лента в режиме без SSE
const STREAM_RETRY_MS = [2_000, 5_000, 10_000, 30_000];
const STREAM_RETRY_FROM_POLL_MS = 60_000;
const HELLO_TIMEOUT_MS = 10_000;
const REACHABLE_TTL_MS = 5 * 60_000;   // кого можно @-упомянуть — перечитываем при возврате во вкладку

let draftSeq = 0;

export function ChannelProvider({ children }) {
  const { can } = usePerm();
  const enabled = can('channel.view');
  const location = useLocation();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [focusId, setFocusId] = useState(null);
  const [scope, setScopeState] = useState(null);       // { propertyId, propertyName, taskId, taskLabel }
  const [unread, setUnread] = useState({ addressed: 0, has_new: false });
  const [mode, setMode] = useState('stream');          // 'stream' | 'poll'
  const [reachable, setReachable] = useState(null);    // Set id людей, до которых дойдёт @ (null — ещё не знаем)
  const [override, setOverride] = useState(null);      // { scope } — Работа из кнопки контакта, поверх контекста страницы
  const [draft, setDraft] = useState(null);            // { nonce, userId, name } — «@Имя» для поля ввода
  const canWrite = can('channel.create');
  const reachableAt = useRef(0);
  const listeners = useRef(new Set());

  const emit = useCallback((evt) => {
    for (const fn of listeners.current) {
      try { fn(evt); } catch { /* слушатель не должен ронять раздачу */ }
    }
  }, []);

  const subscribe = useCallback((fn) => {
    listeners.current.add(fn);
    return () => listeners.current.delete(fn);
  }, []);

  const refreshUnread = useCallback(() => {
    if (!enabled) return;
    api.get('/channel/unread').then((d) => {
      if (d) setUnread({ addressed: d.addressed || 0, has_new: !!d.has_new });
    }).catch(() => {});
  }, [enabled]);

  // ── Бейдж: старт + поллинг + возврат во вкладку ───────────────────────────
  useEffect(() => {
    if (!enabled) return undefined;
    refreshUnread();
    const id = setInterval(refreshUnread, UNREAD_POLL_MS);
    const onVis = () => { if (document.visibilityState === 'visible') refreshUnread(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
  }, [enabled, refreshUnread]);

  // ── Кого можно @-упомянуть: один запрос на сессию + обновление при возврате ──
  const loadReachable = useCallback(() => {
    if (!enabled) return;
    reachableAt.current = Date.now();
    api.get('/channel/reachable').then((d) => {
      if (d && Array.isArray(d.ids)) setReachable(new Set(d.ids.map(Number)));
    }).catch(() => {});
  }, [enabled]);

  useEffect(() => {
    if (!enabled) { setReachable(null); return undefined; }
    loadReachable();
    const onVis = () => {
      if (document.visibilityState === 'visible' && Date.now() - reachableAt.current > REACHABLE_TTL_MS) loadReachable();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [enabled, loadReachable]);

  // ── SSE с откатом на поллинг ──────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return undefined;
    let stopped = false;
    let ctrl = null;
    let timer = null;
    let pollTimer = null;
    let failures = 0;
    let unreadDebounce = null;

    const bumpUnread = () => {
      clearTimeout(unreadDebounce);
      unreadDebounce = setTimeout(refreshUnread, 800);
    };

    const startPolling = () => {
      if (pollTimer) return;
      setMode('poll');
      track('channel_stream_fallback', { failures });
      pollTimer = setInterval(() => { emit({ type: 'poll' }); bumpUnread(); }, FALLBACK_POLL_MS);
      // Время от времени пробуем вернуться на поток.
      timer = setTimeout(connect, STREAM_RETRY_FROM_POLL_MS);
    };

    const stopPolling = () => {
      if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
      setMode('stream');
    };

    const scheduleRetry = () => {
      if (stopped) return;
      failures += 1;
      if (failures >= 2 && !pollTimer) { startPolling(); return; }
      const wait = pollTimer ? STREAM_RETRY_FROM_POLL_MS : STREAM_RETRY_MS[Math.min(failures, STREAM_RETRY_MS.length) - 1];
      timer = setTimeout(connect, wait);
    };

    async function connect() {
      if (stopped) return;
      ctrl = new AbortController();
      let gotHello = false;
      const helloTimer = setTimeout(() => { if (!gotHello) ctrl.abort(); }, HELLO_TIMEOUT_MS);
      try {
        const res = await openChannelStream(ctrl.signal);
        if (!res.ok || !res.body) throw new Error(`stream ${res.status}`);
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const chunks = buf.split('\n\n');
          buf = chunks.pop();
          for (const chunk of chunks) {
            const lines = chunk.split('\n');
            const evName = (lines.find((l) => l.startsWith('event: ')) || '').slice(7);
            const data = lines.filter((l) => l.startsWith('data: ')).map((l) => l.slice(6)).join('\n');
            if (evName === 'hello') {
              gotHello = true;
              failures = 0;
              stopPolling();
              // После переподключения могли пропустить события — пусть лента догрузится.
              emit({ type: 'poll' });
              refreshUnread();
              continue;
            }
            if (!data) continue;
            try {
              const evt = JSON.parse(data);
              emit(evt);
              bumpUnread();
            } catch { /* мусорная строка — пропускаем */ }
          }
        }
      } catch {
        /* обрыв / abort / сеть — решает scheduleRetry */
      } finally {
        clearTimeout(helloTimer);
      }
      if (!stopped) {
        // Чистое закрытие сервером (раз в 30 мин) — сразу переподключаемся.
        if (gotHello) { failures = 0; timer = setTimeout(connect, 500); } else scheduleRetry();
      }
    }

    connect();
    return () => {
      stopped = true;
      clearTimeout(timer);
      clearTimeout(unreadDebounce);
      if (pollTimer) clearInterval(pollTimer);
      if (ctrl) ctrl.abort();
    };
  }, [enabled, emit, refreshUnread]);

  // ── Deep-link: ?channel=<id> (пуш, Telegram, ссылка) ──────────────────────
  useEffect(() => {
    if (!enabled) return;
    const params = new URLSearchParams(location.search);
    const id = Number(params.get('channel'));
    if (!id) return;
    params.delete('channel');
    const rest = params.toString();
    navigate(`${location.pathname}${rest ? `?${rest}` : ''}`, { replace: true, state: location.state });
    setFocusId(id);
    setOpen(true);
    track('channel_opened', { source: 'deeplink' });
  }, [enabled, location.search, location.pathname, location.state, navigate]);

  // ── Предзагрузка ──────────────────────────────────────────────────────────
  // Появилось непрочитанное — заранее тянем код шторки и первую страницу общей
  // ленты: тап по «Рации» открывает её без ожидания. Страница живёт 45 с,
  // дальше шторка грузит сама. Перезапрашиваем только при смене бейджа, а не
  // на каждом 30-секундном опросе.
  const prefetchRef = useRef(null);         // { data, at }
  const prefetchKeyRef = useRef('');
  useEffect(() => {
    if (!enabled || open) return;
    if (!unread.addressed && !unread.has_new) return;
    const key = `${unread.addressed}|${unread.has_new}`;
    if (key === prefetchKeyRef.current) return;
    prefetchKeyRef.current = key;
    import('./ChannelDrawer').catch(() => {});
    api.get('/channel/messages?start=unread&limit=30')
      .then((d) => { if (d) prefetchRef.current = { data: d, at: Date.now() }; })
      .catch(() => {});
  }, [enabled, open, unread.addressed, unread.has_new]);

  /** Забрать свежую предзагруженную страницу общей ленты (один раз). */
  const takePrefetched = useCallback(() => {
    const p = prefetchRef.current;
    prefetchRef.current = null;
    prefetchKeyRef.current = '';
    return p && Date.now() - p.at < 45_000 ? p.data : null;
  }, []);

  const openChannel = useCallback((opts = {}) => {
    setOverride(null);
    setFocusId(opts.messageId || null);
    setOpen(true);
  }, []);
  const closeChannel = useCallback(() => { setOpen(false); setFocusId(null); setOverride(null); setDraft(null); }, []);

  /**
   * Можно ли упомянуть этого человека из контакта Работы: у меня есть Канал и
   * право писать, а у него — право читать Канал (иначе @ уйдёт в пустоту и
   * кнопка выглядела бы как «связался»). Пока список не загружен — false:
   * кнопка остаётся такой, какой была до Канала.
   */
  const canMention = useCallback((userId) => (
    !!(enabled && canWrite && userId && reachable && reachable.has(Number(userId)))
  ), [enabled, canWrite, reachable]);

  /**
   * Открыть Канал на ленте Работы с «@Имя» в поле ввода (текст дописывает
   * человек, сообщение само не уходит).
   * @param {{userId:number, name:string, scope:object}} p  scope — как у useChannelScope
   */
  const mentionInChannel = useCallback(({ userId, name, scope: taskScope }) => {
    draftSeq += 1;
    setOverride(taskScope ? { scope: { owner: 'contact-mention', ...taskScope } } : null);
    setDraft({ nonce: draftSeq, userId, name });
    setFocusId(null);
    setOpen(true);
  }, []);
  const clearDraft = useCallback(() => setDraft(null), []);

  // Страница объявляет контекст; при уходе со страницы — снимает свой.
  const setScope = useCallback((next) => setScopeState(next), []);
  const clearScope = useCallback((owner) => {
    setScopeState((cur) => (cur && cur.owner === owner ? null : cur));
  }, []);

  // Работа из кнопки контакта перекрывает контекст страницы, пока Канал открыт.
  const effScope = override ? override.scope : scope;

  const value = useMemo(() => ({
    enabled, open, focusId, scope: effScope, unread, mode, draft,
    openChannel, closeChannel, setScope, clearScope, subscribe, refreshUnread,
    canMention, mentionInChannel, clearDraft, takePrefetched,
  }), [enabled, open, focusId, effScope, unread, mode, draft, openChannel, closeChannel, setScope, clearScope, subscribe, refreshUnread, canMention, mentionInChannel, clearDraft, takePrefetched]);

  return <ChannelCtx.Provider value={value}>{children}</ChannelCtx.Provider>;
}

/** Контекст Канала; вне провайдера — null (кнопка просто не рисуется). */
export function useChannel() {
  return useContext(ChannelCtx);
}

let scopeSeq = 0;

/**
 * Страница объявляет, в каком контексте открывать Канал.
 *   useChannelScope({ propertyId, propertyName, taskId, taskLabel })
 * null / без propertyId — общая лента.
 */
export function useChannelScope(next) {
  const ch = useChannel();
  const ownerRef = useRef(null);
  if (ownerRef.current === null) { scopeSeq += 1; ownerRef.current = scopeSeq; }
  const setScope = ch && ch.setScope;
  const clearScope = ch && ch.clearScope;
  const propertyId = next && next.propertyId;
  const propertyName = next && next.propertyName;
  const taskId = next && next.taskId;
  const taskLabel = next && next.taskLabel;
  const taskNumber = next && next.taskNumber;
  const taskGroup = next && next.taskGroup;

  useEffect(() => {
    if (!setScope) return;
    const owner = ownerRef.current;
    if (propertyId) {
      setScope({
        owner, propertyId, propertyName,
        taskId: taskId || null, taskLabel: taskLabel || null,
        taskNumber: taskNumber || null, taskGroup: taskGroup || null,
      });
    } else clearScope(owner);
  }, [setScope, clearScope, propertyId, propertyName, taskId, taskLabel, taskNumber, taskGroup]);

  useEffect(() => {
    const owner = ownerRef.current;
    return () => { if (clearScope) clearScope(owner); };
  }, [clearScope]);
}
